import Image from "../Image";
import MobileStepper from "@material-ui/core/MobileStepper";
import React, {useEffect, useRef, useState} from "react";
import {useTheme} from "@material-ui/core/styles";
import lang from "../../core/lang";
import {makeStyles} from "@material-ui/core";
import Button from "../Button";
import * as PropTypes from "prop-types";
import Box from "../Box";
import palette from "../../core/palette";
import logger from "../../core/logger";
import useInterval from "../../hook/useInterval";
import {Icon} from "@iconify/react";
import roundKeyboardArrowRight from "@iconify/icons-ic/round-keyboard-arrow-right"
import roundKeyboardArrowLeft from "@iconify/icons-ic/round-keyboard-arrow-left"

const log = logger.getLogger('component/ImageList');
const useStyles = makeStyles(theme => ({
    imageList: {
        width: props => props.width,
        height: props => props.height,
        borderRadius: theme.shape.borderRadius,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        '&:hover': {
            '& .appfuse-imageList-nextButton': {
                backgroundColor: props => props.nextButtonBackgroundColor,
            },
            '& .appfuse-imageList-backButton': {
                backgroundColor: props => props.backButtonBackgroundColor,
            }
        }
    },
    stepper: {
        position: 'absolute',
        bottom: 0,
        width: '100%',
        backgroundColor: "transparent",
        display: props => props.multiple ? '' : 'none',
        borderRadius: theme.shape.borderRadius,
        '& .MuiMobileStepper-dot': {
            width: '20px',
            height: '10px',
            borderRadius: '50px',
            border: '1px white solid',
            backgroundColor: theme.palette.text.disabled
        },
        '& .MuiMobileStepper-dots': {
            maxWidth: '100%',
            overflow: 'hidden'
        },
        '& .MuiMobileStepper-dotActive': {
            backgroundColor: theme.palette.primary.main
        },
        '&:hover': {
            cursor: 'pointer'
        }
    }
}));

function ImageList(props) {
    const {value = [], fullWidth, fullHeight, transition, direction, autoplay, interval} = props;
    const width = fullWidth ? '100%' : props.width;
    const height = fullHeight ? '100%' : props.height;
    const theme = useTheme();
    const [index, setIndex] = useState(-1);
    const count = value.length;
    const multiple = count > 1;
    const nextButtonBackgroundColor = (index === count - 1) ? undefined : theme.palette.primary.main;
    const backButtonBackgroundColor = (index === 0) ? undefined : theme.palette.primary.main;
    const classes = useStyles({width, height, multiple, nextButtonBackgroundColor, backButtonBackgroundColor});
    const [countdown, setCountdown] = useState(interval);
    const state = useRef({count, countdown, index});

    useEffect(() => {
        state.current = {count, countdown, index};
    }, [value, countdown, index]);

    useInterval(() => {
        const {count, countdown, index} = state.current;
        if(!autoplay || count === 0) return;
        if (countdown > 0) {
            setCountdown(countdown - 1000);
            return;
        }
        const nextIndex = (index === count - 1) ? 0 : index + 1;
        props.onChange && props.onChange({index: nextIndex});
        setIndex(nextIndex);
    }, 1000)

    useEffect(() => {
        setIndex(props.index || (value.length>0 ? 0 : -1));
    }, [props.value]);

    useEffect(() => {
        setCountdown(interval);
    }, [index])

    const handleClick = index => event => {
        event.stopPropagation();
        setIndex(index);
        props.onChange && props.onChange({index});
    }

    const options = lang.omit(props, 'value', 'height', 'width');
    const src = value[index];
    const stepButtonBackgroundColor = palette.color(theme.palette.primary.main, 0.5);
    const stepper = props.stepper || (
        <MobileStepper
            className={classes.stepper}
            steps={count}
            variant="dots"
            activeStep={index}
            onClick={event=>event.stopPropagation()}
            nextButton={
                <Box marginRight={2}>
                    <Button size="small" variant="icon"  className="appfuse-imageList-nextButton"
                            icon={<Icon icon={roundKeyboardArrowRight} />} color={theme.palette.primary.contrastText}
                            backgroundColor={stepButtonBackgroundColor}
                            disabled={index === count - 1} onClick={handleClick(index + 1)} />
                </Box>

            }
            backButton={
                <Box marginLeft={2}>
                    <Button size="small" variant="icon" className="appfuse-imageList-backButton"
                            icon={<Icon icon={roundKeyboardArrowLeft} />} color={theme.palette.primary.contrastText}
                            backgroundColor={stepButtonBackgroundColor}
                            onClick={handleClick(index - 1)} disabled={index === 0} />
                </Box>
            }
        />
    );
    return (
        <div className={classes.imageList} onClick={props.onClick}>
            <Image value={src} title={"Unknown Image"} aria-label={"Unknown Image"} {...options} fullHeight fullWidth />
            {stepper}
        </div>
    )
}

ImageList.propTypes = {
    className: PropTypes.string,
    style: PropTypes.object,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(File),
        PropTypes.object,
        PropTypes.arrayOf(PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.instanceOf(File),
            PropTypes.object,
        ]))
    ]),
    onChange: PropTypes.func,
    fullWidth: PropTypes.bool,
    fullHeight: PropTypes.bool,
    maxFileSize: PropTypes.number,
    width: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    height: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    objectFit: PropTypes.oneOf(['cover', 'contain', 'fill', 'none', 'auto', 'width', 'height']),
    borderRadius: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    stepper: PropTypes.element,
    transition: PropTypes.oneOf(['fade', 'grow', 'zoom', 'slide']),
    direction: PropTypes.oneOf(['up', 'down', 'left', 'right']),
    interval: PropTypes.number,
    autoplay: PropTypes.bool,
}

ImageList.defaultProps = {
    interval: 2000,
    autoplay: false
}

export default ImageList;